import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Lock, Infinity as InfinityIcon, ShieldCheck } from "lucide-react";
import { HeroScene } from "./HeroScene";
import { SplitLines, EASE } from "./motion";
import { ASSETS } from "@/lib/assets";

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden line-grid">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,rgba(10,124,255,0.10),transparent_55%)]" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--bg)]" />
      <HeroScene scroll={scrollYProgress} />

      <motion.div style={{ y: textY, opacity: textOpacity }} className="relative z-10 mx-auto max-w-7xl px-6 pt-36 sm:pt-44 pb-28 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-9">
          <motion.div {...fade(0.3)} className="eyebrow mb-8">Da sua IA preferida para o ar, em minutos</motion.div>
          <SplitLines
            data-testid="hero-headline"
            className="font-display font-bold tracking-[-0.035em] leading-[0.95] text-[clamp(2.5rem,5.4vw,5rem)] text-[var(--ink)]"
            lines={["Sua IA gera a página.", "O Lowpages coloca", <>no <span className="mark">ar em segundos.</span></>]}
            delay={0.45}
          />
          <motion.p {...fade(0.95)} data-testid="hero-subtitle" className="mt-8 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Cole o HTML gerado pelo ChatGPT ou Claude, edite clicando e publique com um clique.
            <span className="text-[var(--ink)] font-semibold"> Sem hospedagem, sem DNS, sem cPanel, sem programar.</span>
          </motion.p>
          <motion.div {...fade(1.1)} className="mt-10 flex flex-wrap items-center gap-4">
            <a href="/register" data-testid="hero-cta-primary" className="btn btn-primary ring-pulse">
              Começar grátis <ArrowRight size={18} className="arrow" />
            </a>
            <a href="#como-funciona" data-testid="hero-cta-secondary" className="btn btn-ghost">
              Ver como funciona <ArrowDown size={16} className="arrow" />
            </a>
          </motion.div>
          <motion.ul {...fade(1.3)} data-testid="hero-trust-badges" className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm text-slate-500">
            <li className="flex items-center gap-2"><Lock size={14} className="text-[var(--blue)]" /> SSL + CDN inclusos</li>
            <li className="flex items-center gap-2"><InfinityIcon size={14} className="text-[var(--blue)]" /> Visitas e leads ilimitadas</li>
            <li className="flex items-center gap-2"><ShieldCheck size={14} className="text-[var(--blue)]" /> Cancele quando quiser</li>
          </motion.ul>
        </div>
      </motion.div>

      <motion.div {...fade(1.6)} className="absolute bottom-8 left-6 sm:left-10 z-10 hidden md:flex items-center gap-3 font-mono text-[11px] tracking-[0.2em] uppercase text-slate-400">
        <span className="h-8 w-px bg-slate-300 origin-top animate-pulse" /> role para descobrir
      </motion.div>
    </section>
  );
};

export const ProductStage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "center center"] });
  const rotateX = useTransform(scrollYProgress, [0, 1], [26, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [0.88, 1]);
  const y = useTransform(scrollYProgress, [0, 1], [60, 0]);
  const shadow = useTransform(scrollYProgress, [0, 1], [0.05, 0.25]);

  return (
    <section ref={ref} data-testid="product-stage" className="relative -mt-24 sm:-mt-32 pb-16 px-4 sm:px-6">
      <div className="mx-auto max-w-6xl" style={{ perspective: 1600 }}>
        <motion.div style={{ rotateX, scale, y, transformStyle: "preserve-3d" }} className="relative">
          <motion.div style={{ opacity: shadow }} className="absolute inset-x-[10%] -bottom-8 h-32 rounded-full bg-[var(--blue)] blur-3xl" />
          <img src={ASSETS.laptop} alt="Painel Meus projetos do Lowpages em um MacBook Pro" fetchpriority="high" decoding="async" width="1536" height="1024" className="relative w-full h-auto select-none" draggable={false} />
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.8, ease: EASE }}
            data-testid="stage-badge-published"
            className="absolute right-[6%] top-[14%] glass rounded-2xl px-4 py-3 shadow-xl flex items-center gap-3"
          >
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <div className="leading-tight">
              <div className="text-xs font-semibold text-[var(--ink)]">publicado</div>
              <div className="font-mono text-[10px] text-slate-500">/p/protocolo-volta-barriga</div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.8, ease: EASE }}
            data-testid="stage-badge-time"
            className="absolute left-[4%] bottom-[28%] rounded-2xl bg-[var(--ink)] text-white px-4 py-3 shadow-xl"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">tempo até o ar</div>
            <div className="font-display font-bold text-2xl leading-none mt-1">2m 41s <span className="text-[var(--orange)]">⚡</span></div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
